import { Search } from '@snack-uikit/search';
import { useEffect } from 'react';
import { Wrapper } from 'src/components/Wrapper';
import { useSearchParams } from 'src/contexts/SearchParams/useSearchParams';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useElasticProductsSearch } from 'src/hooks/useElasticProductsSearch';
import { useFilteredProducts } from 'src/hooks/useFilteredProducts';
import { useWindowWidth } from 'src/hooks/useWindowWidth';
import { type ClickAnalyticsType } from 'src/types';
import {
  type CatalogProduct,
  type ProductCategory,
  type ProductPlatform,
} from 'src/types/backendContent';

import { CategoryList } from '../CategoryList';
import { MobileCategorySelect } from '../MobileCategorySelect';
import { PlatformSelect } from '../PlatformSelect';
import { ProductsList } from '../ProductsList';
import s from './ProductsCatalog.module.scss';

const ALL_PLATFORMS: CatalogProduct['productPlatforms'][number] = {
  id: '',
  title: 'Все платформы' as ProductPlatform,
  slug: '',
};

type ProductsCatalogProps = {
  platforms: CatalogProduct['productPlatforms'];
  categories: ProductCategory[];
  products: CatalogProduct[];
};

export function ProductsCatalog({
  platforms,
  categories,
  products,
}: ProductsCatalogProps) {
  const searchParams = useSearchParams();

  // TODO: Заменить на хук из uikit-product, когда починят
  const { windowWidth } = useWindowWidth();

  const { clickAnalytics } = useAnalytics();

  const {
    categoriesByPlatform,
    filteredProducts,
    platformFilter = '',
    categoryFilter,
    titleFilter,
    setPlatformFilter,
    setCategoryFilter,
    setTitleFilter,
  } = useFilteredProducts({
    categories,
    products,
  });

  const { searched, loading, debouncedPhrase } = useElasticProductsSearch({
    phrase: titleFilter.trim(),
    products: products,
    filteredProducts: filteredProducts,
    platform: platformFilter,
    typeAnalytics: 'live-page-products',
  });

  const isNotDesktop = windowWidth && windowWidth < 1024;

  const CategorySelect = isNotDesktop ? MobileCategorySelect : CategoryList;

  useEffect(() => {
    const platformParam = searchParams.get('platform');

    if (typeof platformParam === 'string') {
      setPlatformFilter(platformParam);
    }

    const categoryParam = searchParams.get('category');

    if (typeof categoryParam === 'string') {
      setCategoryFilter(categoryParam);
    }

    const searchParam = searchParams.get('search');

    if (typeof searchParam === 'string') {
      setTitleFilter(searchParam);
    }
  }, [searchParams, setCategoryFilter, setPlatformFilter, setTitleFilter]);

  const allPlatforms = [ALL_PLATFORMS, ...platforms];

  const handlePlatformChange = (value: string) => {
    const analyticsOptions: ClickAnalyticsType = {
      action: 'click',
      clickZone: 'body',
      clickElement: 'filter-select',
      clickContent: value,
      uniqueId: `products-catalog-platform-${value}`,
      transitionType: 'inside-link',
    };

    clickAnalytics(analyticsOptions);

    setPlatformFilter(value);
  };

  const handleCategoryChange = (value: string) => {
    const analyticsOptions: ClickAnalyticsType = {
      action: 'click',
      clickZone: 'body',
      clickElement: 'filter-select',
      clickContent: value,
      uniqueId: `products-catalog-category-${value}`,
      transitionType: 'inside-link',
    };

    clickAnalytics(analyticsOptions);

    setCategoryFilter(value);
  };

  const handleCategoryOnOpenChange = () => {
    clickAnalytics({
      action: 'click',
      clickZone: 'body',
      clickElement: 'filter-select',
      clickContent: 'Категория',
      uniqueId: `products-catalog-on-click-filter-category-Категория`,
      transitionType: 'inside-link',
    });
  };

  const handleFocusSearch = () => {
    clickAnalytics({
      action: 'click',
      clickZone: 'body',
      clickElement: 'input',
      clickContent: 'Поиск продукта',
      uniqueId: 'products-catalog-search-input',
      transitionType: 'inside-link',
    });
  };

  const handleChangeFilter = (value: string) => {
    value.trim().length > 180
      ? setTitleFilter(value.slice(0, 180))
      : setTitleFilter(value);
  };

  return (
    <Wrapper className={s.root}>
      <div className={s.catalog}>
        <div className={s.catalogFilters}>
          <PlatformSelect
            platforms={allPlatforms}
            value={platformFilter}
            onChange={handlePlatformChange}
          />
          <CategorySelect
            categories={categoriesByPlatform}
            value={categoryFilter}
            onChange={handleCategoryChange}
            onOpenChange={handleCategoryOnOpenChange}
          />
        </div>
        <div className={s.productsList}>
          <Search
            className={s.catalogSearch}
            placeholder="Поиск продукта"
            size="l"
            value={titleFilter}
            onChange={handleChangeFilter}
            onFocus={handleFocusSearch}
          />
          <ProductsList
            categoryFilter={categoryFilter}
            categories={categories}
            /**
             * FIXME: Добавлено потому, что при клиентском роутинге из шапки
             * useElasticProductsSearch возвращает не отфильтрованный список
             * продуктов. Необходимо внести правки в хук.
             */
            products={!titleFilter ? filteredProducts : searched}
            loading={loading}
            searchedPhrase={debouncedPhrase}
          />
        </div>
      </div>
    </Wrapper>
  );
}
